import { useDeferredValue, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import {
  providerApi,
  type BookingStatus,
  type ProviderCustomerDetailResponse,
  type ProviderCustomerRecord,
} from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { useMediaQuery } from "../../lib/use-media-query";

function providerLocale(language: "en" | "ar") {
  return language === "ar" ? "ar-EG" : "en-US";
}

function formatDate(value: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

/** "Today", "Yesterday", "Sep 26", or "Sep 26, 2025" for older years. */
function formatLastSeen(value: string, locale: string, t: (key: string) => string) {
  const date = new Date(value);
  const today = new Date();
  const dayStart = (item: Date) => new Date(item.getFullYear(), item.getMonth(), item.getDate()).getTime();
  const days = Math.round((dayStart(today) - dayStart(date)) / 86_400_000);
  if (days === 0) return t("Today");
  if (days === 1) return t("Yesterday");
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === today.getFullYear() ? undefined : "numeric",
  }).format(date);
}

function formatDateTime(value: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function StarRating({ stars }: { stars: number }) {
  return (
    <span className="text-yellow-400 text-xs tracking-tight">
      {"★".repeat(stars)}{"☆".repeat(5 - stars)}
    </span>
  );
}

const BOOKING_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  confirmed: "Confirmed",
  cancelled: "Cancelled",
  rejected: "Rejected",
  no_show: "No-show",
  checked_in: "Checked in",
  completed: "Completed",
  expired: "Expired",
};

const BOOKING_STATUS_COLORS: Record<string, string> = {
  pending: "text-yellow-400",
  confirmed: "text-blue-400",
  cancelled: "text-slate-400",
  rejected: "text-red-400",
  no_show: "text-red-300",
  checked_in: "text-cyan-400",
  completed: "text-green-400",
  expired: "text-slate-400",
};

function useProviderCustomers(search: string) {
  const { t } = useAppPreferences();
  const [customers, setCustomers] = useState<ProviderCustomerRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    providerApi
      .listCustomers(search || undefined)
      .then((data) => {
        if (!cancelled) {
          setCustomers(data.customers);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t("Could not load customer list."));
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [search, t]);

  return { customers, isLoading, error };
}

function useCustomerDetail(customer: ProviderCustomerRecord | null) {
  const { t } = useAppPreferences();
  const [detail, setDetail] = useState<ProviderCustomerDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!customer) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setDetail(null);

    providerApi
      .getCustomerDetail({
        customerUserId: customer.customer_user_id,
        customerEmail: customer.customer_email ?? undefined,
        customerPhone: customer.customer_phone ?? undefined,
        customerName: customer.customer_name,
      })
      .then((data) => {
        if (!cancelled) {
          setDetail(data);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t("Could not load customer detail."));
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [customer, t]);

  return { detail, isLoading, error };
}

function BookingsByStatus({ bookings }: { bookings: ProviderCustomerDetailResponse["bookings"] }) {
  const { resolvedLanguage, t } = useAppPreferences();
  const locale = providerLocale(resolvedLanguage);
  const groups: Record<string, typeof bookings> = {};
  for (const b of bookings) {
    const key = b.status as string;
    if (!groups[key]) groups[key] = [];
    groups[key].push(b);
  }

  const statusOrder: BookingStatus[] = ["confirmed", "checked_in", "completed", "pending", "no_show", "cancelled", "rejected", "expired"];
  const orderedKeys = [...statusOrder.filter((s) => groups[s]), ...Object.keys(groups).filter((k) => !statusOrder.includes(k as BookingStatus))];

  return (
    <div className="grid gap-2">
      {orderedKeys.map((key) => (
        <div key={key}>
          <p className={`text-[10px] font-semibold uppercase tracking-[0.13em] ${BOOKING_STATUS_COLORS[key] ?? "text-slate-400"}`}>
            {t(BOOKING_STATUS_LABELS[key] ?? key)} ({(groups[key] ?? []).length})
          </p>
          <div className="mt-1 grid gap-1">
            {(groups[key] ?? []).map((b) => (
              <div key={b.id} className="ops-record ops-record-dense flex flex-wrap items-center justify-between gap-2">
                <span className="truncate text-xs text-slate-300">{b.venue_name} · {b.unit_name}</span>
                <span className="text-xs text-slate-400">{formatDateTime(b.scheduled_start_at, locale)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CustomerDetailPanel({ customer }: { customer: ProviderCustomerRecord }) {
  const { resolvedLanguage, t } = useAppPreferences();
  const locale = providerLocale(resolvedLanguage);
  const { detail, isLoading, error } = useCustomerDetail(customer);

  if (isLoading) {
    return <p className="ops-copy text-xs">{t("Loading detail…")}</p>;
  }

  if (error) {
    return <p className="text-xs text-red-400">{error}</p>;
  }

  if (!detail) return null;

  return (
    <div className="grid gap-3">
      <div>
        <p className="ops-kicker text-xs">{t("Bookings")} ({detail.bookings.length})</p>
        {detail.bookings.length === 0 ? (
          <p className="mt-1 ops-copy text-xs">{t("No bookings.")}</p>
        ) : (
          <BookingsByStatus bookings={detail.bookings} />
        )}
      </div>

      {detail.sessions.length > 0 && (
        <div>
          <p className="ops-kicker text-xs">{t("Sessions")} ({detail.sessions.length})</p>
          <div className="mt-1 grid gap-1">
            {detail.sessions.map((s) => (
              <div key={s.id} className="ops-record ops-record-dense flex flex-wrap items-center justify-between gap-2">
                <span className="truncate text-xs text-slate-300">{s.venue_name} · {s.unit_name}</span>
                <span className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                  {formatDateTime(s.started_at, locale)}
                  <span className="text-slate-500">
                    {t("{minutes} min", { minutes: s.duration_minutes })}
                    {s.is_overtime ? <span className="text-orange-400"> {t("+{minutes} min overtime", { minutes: s.overtime_minutes })}</span> : null}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {detail.reviews.length > 0 && (
        <div>
          <p className="ops-kicker text-xs">{t("Reviews")} ({detail.reviews.length})</p>
          <div className="mt-1 grid gap-1">
            {detail.reviews.map((r) => (
              <div key={r.id} className="ops-record ops-record-dense grid gap-0.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <StarRating stars={r.stars} />
                  <span className="text-xs text-slate-500">{r.venue_name} · {formatDate(r.created_at, locale)}</span>
                </div>
                {r.comment && <p className="text-xs text-slate-300">{r.comment}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function makeKey(c: ProviderCustomerRecord) {
  return `${c.customer_user_id ?? ""}|${c.customer_email ?? ""}|${c.customer_name}|${c.customer_phone ?? ""}`;
}

export function ProviderCustomersCard() {
  const { resolvedLanguage, t } = useAppPreferences();
  const locale = providerLocale(resolvedLanguage);
  const [searchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(
    () =>
      searchParams.get("customer_email") ??
      searchParams.get("customer_phone") ??
      searchParams.get("customer_name") ??
      "",
  );
  const search = useDeferredValue(searchInput.trim());
  const { customers, isLoading, error } = useProviderCustomers(search);
  const [selectedCustomer, setSelectedCustomer] = useState<ProviderCustomerRecord | null>(null);

  const selectedKey = selectedCustomer ? makeKey(selectedCustomer) : null;
  // Wide screens show details beside the list; phones show them under the tapped row.
  const isWide = useMediaQuery("(min-width: 1024px)");

  // Deep links (e.g. "View customer" on a booking) carry the customer's
  // identifiers — auto-select the exact record once the list resolves.
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current || isLoading || customers.length === 0) {
      return;
    }
    const name = searchParams.get("customer_name");
    if (!name) {
      deepLinkAppliedRef.current = true;
      return;
    }
    const email = searchParams.get("customer_email") ?? "";
    const phone = searchParams.get("customer_phone") ?? "";
    const match = customers.find(
      (customer) => searchParams.get("customer_user_id")
        ? customer.customer_user_id === searchParams.get("customer_user_id")
        : `${customer.customer_email ?? ""}|${customer.customer_name}|${customer.customer_phone ?? ""}` === `${email}|${name}|${phone}`,
    );
    if (match) {
      setSelectedCustomer(match);
    }
    deepLinkAppliedRef.current = true;
  }, [customers, isLoading, searchParams]);

  return (
    <div className="grid gap-4">
      <header className="min-w-0">
        <p className="ops-kicker">{t("Regulars and guests")}</p>
        <h1 className="ops-page-title">{t("Customers")}</h1>
        <p className="ops-copy mt-1 text-sm">
          {isLoading
            ? t("Everyone who has booked or played here.")
            : t(customers.length === 1 ? "{count} person has booked or played here." : "{count} people have booked or played here.", { count: customers.length })}
        </p>
      </header>
    <div className="ops-surface">

      <div className="grid gap-3 lg:grid-cols-[1fr_1fr]">
        <div className="grid gap-3 content-start">
          <input
            className="ops-input w-full"
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("Search by name, email, or phone…")}
            type="search"
            value={searchInput}
          />

          {isLoading ? (
            <p className="ops-copy text-xs">{t("Loading…")}</p>
          ) : error ? (
            <p className="text-sm text-red-400">{error}</p>
          ) : customers.length === 0 ? (
            <p className="ops-copy text-xs">
              {search
                ? t("No customers match that search.")
                : t("No bookings yet — customers will appear here once they book.")}
            </p>
          ) : (
            <div className="grid gap-1.5">
              {customers.map((customer) => {
                const key = makeKey(customer);
                const isSelected = key === selectedKey;

                return (
                  <div className="grid gap-1.5" key={key}>
                  <button
                    aria-expanded={isSelected}
                    data-active={isSelected}
                    type="button"
                    className={`ops-record ops-record-dense ops-interactive-record flex items-center justify-between gap-2 text-start transition ${
                      isSelected
                        ? "border-[rgba(91,44,255,0.45)] bg-[rgba(91,44,255,0.12)]"
                        : "hover:border-[rgba(91,44,255,0.30)]"
                    }`}
                    onClick={() => setSelectedCustomer(isSelected ? null : customer)}
                  >
                    <div className="grid min-w-0 gap-0.5">
                      <span className="truncate text-sm font-medium text-white">{customer.customer_name}</span>
                      <span className="truncate text-xs text-slate-400">
                        {[customer.customer_email, customer.customer_phone].filter(Boolean).join(" · ") || t("No contact")}
                      </span>
                    </div>
                    <div className="grid shrink-0 gap-0.5 text-end">
                      <span className="whitespace-nowrap text-xs font-semibold text-white">
                        {t(customer.booking_count === 1 ? "{count} booking" : "{count} bookings", { count: customer.booking_count })}
                      </span>
                      <span className="whitespace-nowrap text-xs text-slate-400">{formatLastSeen(customer.last_booking_at, locale, t)}</span>
                    </div>
                  </button>
                  {/* On phones the details open right under the row instead of below the whole list. */}
                  {isSelected && !isWide ? (
                    <div className="ops-subtle-surface p-3">
                      <CustomerDetailPanel customer={customer} />
                    </div>
                  ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {isWide ? <div className="ops-subtle-surface content-start p-3">
          {selectedCustomer ? (
            <CustomerDetailPanel customer={selectedCustomer} />
          ) : (
            <div className="ops-empty py-6 text-center">
              <p className="ops-copy text-xs">
                {t("Select a customer to view bookings, sessions, and reviews.")}
              </p>
            </div>
          )}
        </div> : null}
      </div>
    </div>
    </div>
  );
}
